import axios from 'axios'
import type { IDPProvidersResponse, DBLoginRequest, SessionStatus } from '../types/auth'

// In dev, Vite proxies /auth/* to FastAPI at http://localhost:8001.
// In production, this SPA is served by FastAPI itself so it's same-origin.
const apiClient = axios.create({
  baseURL: '',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

export async function getIDPProviders(): Promise<IDPProvidersResponse> {
  const response = await apiClient.get<IDPProvidersResponse>('/auth/idp-providers')
  return response.data
}

export async function postDBLogin(credentials: DBLoginRequest): Promise<void> {
  await apiClient.post('/auth/db-login', credentials)
}

export async function getSessionStatus(): Promise<SessionStatus> {
  const response = await apiClient.get<SessionStatus>('/auth/session/status')
  return response.data
}

/** Copy the Auth Service session onto another localhost origin (UI Vite /auth proxy). */
export function buildSessionHandoffUrl(returnUrl: string): string {
  return `/auth/session/handoff?return_url=${encodeURIComponent(returnUrl)}`
}

function originOf(url: string): string {
  try {
    return new URL(url).origin
  } catch {
    return ''
  }
}

function hostsShareCookieParent(a: string, b: string): boolean {
  try {
    const ha = new URL(a).hostname.toLowerCase()
    const hb = new URL(b).hostname.toLowerCase()
    // Cloud: Auth and Deep Synthesis both sit under .neuroiq.ai with Domain=.neuroiq.ai.
    if (
      (ha === 'neuroiq.ai' || ha.endsWith('.neuroiq.ai')) &&
      (hb === 'neuroiq.ai' || hb.endsWith('.neuroiq.ai'))
    ) {
      return true
    }
  } catch {
    /* ignore */
  }
  return false
}

/**
 * Home (:3000) has no /auth proxy — never send it to /auth/session/handoff.
 * Local Deep Synthesis UI (:5173) proxies /auth, so it needs the ticket handoff.
 * Cloud UI already receives Domain=.neuroiq.ai cookies from Auth — no ticket.
 */
export function shouldUseSessionHandoff(returnUrl: string, homeAppUrl?: string | null): boolean {
  if (!/^https?:\/\//i.test(returnUrl)) {
    return false
  }
  const returnOrigin = originOf(returnUrl)
  if (!returnOrigin || returnOrigin === window.location.origin) {
    return false
  }
  if (homeAppUrl && originOf(homeAppUrl) === returnOrigin) {
    return false
  }
  if (/^https?:\/\/(localhost|127\.0\.0\.1):3000$/i.test(returnOrigin)) {
    return false
  }
  if (hostsShareCookieParent(returnOrigin, window.location.origin)) {
    return false
  }
  return true
}

function resolvePostLoginRedirect(): string | undefined {
  const raw = new URLSearchParams(window.location.search).get('redirect')
  if (!raw) {
    return undefined
  }
  if (/^https?:\/\//i.test(raw)) {
    return raw
  }
  if (raw.startsWith('/')) {
    // Prefer document.referrer origin (Admin / Main UI). With
    // Referrer-Policy: strict-origin-when-cross-origin the referrer is often
    // only the origin — that is enough to resolve a relative path.
    const referrer = document.referrer
    if (referrer) {
      try {
        return new URL(raw, new URL(referrer).origin).href
      } catch {
        /* ignore invalid referrer */
      }
    }
  }
  return undefined
}

/** Start SSO/OIDC login. Optional redirect_url returns to the calling app route. */
export function buildIDPLoginUrl(providerKey: string): string {
  const path = `/auth/login/${providerKey}`
  const redirectUrl = resolvePostLoginRedirect()
  if (!redirectUrl) {
    return path
  }
  return `${path}?redirect_url=${encodeURIComponent(redirectUrl)}`
}
